import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import Reveal from "@/components/shared/Reveal";
import Seo from "@/components/shared/Seo";
import Btn from "@/components/shared/Btn";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const DevenirPartenaire = () => {
  const { data: editions = [] } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const ed2028 = editions.find((e) => e.status === "upcoming");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", function: "", gdpr_consent: false });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) return toast.error("Merci d'accepter le traitement de vos données.");
    setSending(true);
    try {
      await api.post("/registrations", { ...form, source: "partenaire", profile_type: "industriel", interest_only: true, edition_id: ed2028?.id });
      toast.success("Votre demande a bien été envoyée. Nous revenons vers vous sous 24h.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", function: "", gdpr_consent: false });
    } catch { toast.error("Une erreur est survenue."); }
    setSending(false);
  };

  const formulas = [
    { name: "Présence", items: ["Espace dédié dans la zone networking", "Logo sur les supports de communication", "Accès participants pour votre équipe", "Visibilité digitale : site, emailings"], note: "Idéal pour rencontrer et échanger directement avec les participants" },
    { name: "Prise de parole", items: ["Espace dédié dans la zone networking", "Logo sur les supports de communication", "Prise de parole ou table ronde : cas projet concret", "Co-construction du contenu avec FACADES2build", "Mention dans le programme officiel", "Visibilité renforcée avant, pendant et après"], note: "Pour partager votre expertise sur un cas concret" },
    { name: "Partenaire principal", items: ["Association au nom de l'événement (cobranding)", "Logo grand format : signalétique, écrans, programme", "Emplacement networking prioritaire", "Prise de parole incluse", "Diffusion post-événement : replay et newsletter BuildingLab"], note: "Partenariat stratégique — nombre limité", premium: true },
  ];

  return (
    <>
      <Seo title="Devenir partenaire — FACADES2build" description="Rejoignez l'événement de référence des acteurs de la façade et rencontrez une audience qualifiée de prescripteurs." />
      <section className="bg-f2b-black pt-40 pb-16">
        <div className="f2b-container">
          <Reveal><p className="f2b-kicker text-f2b-cyan mb-4">FACADES2build · Partenariat</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-display font-bold text-white text-4xl md:text-6xl leading-tight max-w-3xl">Devenir <span className="italic text-f2b-cyan">partenaire</span></h1></Reveal>
          <Reveal delay={0.16}><p className="text-white/70 mt-6 max-w-2xl">Rejoignez l'événement de référence des acteurs de la façade et rencontrez une audience qualifiée de prescripteurs : architectes, ingénieurs, bureaux d'études et maîtres d'ouvrage.</p></Reveal>
        </div>
      </section>

      {/* audience */}
      <section className="py-16 bg-f2b-black border-t border-white/10">
        <div className="f2b-container grid grid-cols-2 md:grid-cols-4 gap-8">
          {[["Architectes", "39,6%"], ["Ingénieurs", "31,6%"], ["Entreprises façades", "19,4%"], ["Autres", "6,5%"]].map(([l, v], i) => (
            <Reveal key={l} delay={i * 0.06} className="border-l-2 border-f2b-cyan pl-5">
              <div className="font-display font-bold text-white text-3xl md:text-4xl">{v}</div>
              <div className="text-f2b-gray text-sm mt-1 uppercase tracking-wider">{l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* formulas */}
      <section className="py-20 bg-white">
        <div className="f2b-container">
          <Reveal><p className="f2b-kicker text-f2b-gray mb-3">Les formules</p><h2 className="font-display font-bold text-f2b-black text-3xl md:text-4xl mb-10">Modalités de partenariat</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {formulas.map((f, i) => (
              <Reveal key={f.name} delay={i * 0.08} className={`p-8 border flex flex-col ${f.premium ? "border-f2b-gold bg-f2b-black text-white" : "border-f2b-filet"}`}>
                <span className={`f2b-kicker mb-2 ${f.premium ? "text-f2b-gold" : "text-f2b-cyan"}`}>Formule</span>
                <h3 className={`font-display font-bold text-2xl mb-5 ${f.premium ? "text-white" : "text-f2b-black"}`}>{f.name}</h3>
                <ul className="space-y-2 flex-1">
                  {f.items.map((it) => (
                    <li key={it} className={`flex items-start gap-2 text-sm ${f.premium ? "text-white/80" : "text-f2b-text/80"}`}><span className="text-f2b-cyan mt-0.5">—</span>{it}</li>
                  ))}
                </ul>
                <p className={`f2b-lead text-sm mt-5 ${f.premium ? "text-white/60" : "text-f2b-gray"}`}>{f.note}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* form */}
      <section className="py-20 bg-f2b-light">
        <div className="f2b-container grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="f2b-kicker text-f2b-gray mb-3">Parlons-en</p>
            <h2 className="font-display font-bold text-f2b-black text-3xl md:text-4xl">Manifestez votre intérêt</h2>
            <p className="text-f2b-text/75 mt-5 leading-relaxed">Les formules sont définies en échange direct, en fonction de vos objectifs. Ce formulaire vous permet de prendre un premier contact — nous revenons vers vous sous 24h. Aucun engagement à ce stade.</p>
            <a href="mailto:contact@facades2build.com" className="inline-block mt-6 text-f2b-cyan f2b-link-underline">contact@facades2build.com</a>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="bg-white p-8 border border-f2b-filet space-y-4" data-testid="partner-form">
              <div className="grid grid-cols-2 gap-4">
                <Input required placeholder="Prénom *" value={form.first_name} onChange={set("first_name")} data-testid="partner-firstname" />
                <Input required placeholder="Nom *" value={form.last_name} onChange={set("last_name")} data-testid="partner-lastname" />
              </div>
              <Input required type="email" placeholder="E-mail *" value={form.email} onChange={set("email")} data-testid="partner-email" />
              <Input placeholder="Téléphone" value={form.phone} onChange={set("phone")} data-testid="partner-phone" />
              <Input required placeholder="Nom de l'entreprise *" value={form.company} onChange={set("company")} data-testid="partner-company" />
              <Textarea placeholder="Votre message" value={form.function} onChange={set("function")} rows={3} data-testid="partner-message" />
              <label className="flex items-start gap-3 text-sm text-f2b-gray">
                <Checkbox checked={form.gdpr_consent} onCheckedChange={(v) => setForm((f) => ({ ...f, gdpr_consent: !!v }))} data-testid="partner-gdpr" />
                <span>J'accepte que mes données soient utilisées pour traiter ma demande de partenariat (RGPD).</span>
              </label>
              <Btn type="submit" variant="cyan" className="w-full" disabled={sending} data-testid="partner-submit">{sending ? "Envoi…" : "Envoyer ma demande"}</Btn>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default DevenirPartenaire;
